/**
 * Generate a thumbnail image (first page) from a PDF URL (e.g. same-origin download API).
 * Used by Job Board carousel and photo timelines (Homeowner, Hub, Volunteers Group) to show
 * a PDF preview instead of a generic icon.
 */

import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'

const THUMB_SCALE = 1.5 // Balance quality vs size for thumbnail

function ensureWorkerSrc(): void {
  if (typeof window === 'undefined') return
  if (GlobalWorkerOptions.workerSrc) return
  GlobalWorkerOptions.workerSrc =
    'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.mjs'
}

/**
 * Fetch PDF from url, render first page to a JPEG data URL (thumbnail).
 * Runs in browser only. Use a same-origin path (e.g. /api/photos/123/download) to avoid CORS.
 * Rejects on fetch or PDF.js errors.
 */
export async function pdfUrlToThumbnailDataUrl(pdfUrl: string): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error('pdfUrlToThumbnailDataUrl must run in the browser')
  }
  ensureWorkerSrc()

  const res = await fetch(pdfUrl, { mode: 'cors' })
  if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`)
  const arrayBuffer = await res.arrayBuffer()

  const pdf = await getDocument({ data: arrayBuffer }).promise
  const page = await pdf.getPage(1)
  const viewport = page.getViewport({ scale: THUMB_SCALE })
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get canvas context')
  canvas.width = viewport.width
  canvas.height = viewport.height
  await page.render({
    canvasContext: ctx,
    viewport,
    intent: 'display',
  }).promise
  return canvas.toDataURL('image/jpeg', 0.85)
}
